/**
 * Cognito MFA settings (ENG-6107): what the security settings page reads and
 * changes for the signed-in Cognito user. Failures are thrown as
 * `CognitoSignInError` with the webapp's error codes, like the sign-in wrapper.
 */
import {
  type UpdateMFAPreferenceInput,
  confirmUserAttribute,
  fetchMFAPreference,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
  updateMFAPreference,
} from 'aws-amplify/auth';

import { gengql } from '@/__generated__';
import config from '@/config';
import { type GraphQLClient, firstErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';

import { PREFERENCE_KEY, preferOnly } from './cognitoMfaPreference';
import { type CognitoMfaType, CognitoSignInError, mapError } from './cognitoSignIn';

export interface CognitoMfaState {
  enabled: CognitoMfaType[];
  preferred?: CognitoMfaType;
  email?: string;
}

export const PHONE_TAKEN_MESSAGE = 'This number is already used by another account.';
export const PHONE_FORMAT_MESSAGE =
  'Enter the number with its country code, for example +1 555 555 0123';
export const PHONE_UNTEXTABLE_MESSAGE =
  'We could not text this number. Check it, or use another mobile number.';
export const CODE_EXPIRED_MESSAGE = 'This code has expired. Change the number to send a new one.';
export const EMAIL_CODE_EXPIRED_MESSAGE = 'This code has expired. Resend a new one.';

const SEND_PHONE_CODE = gengql(/* GraphQL */ `
  mutation cognitoMfaSendPhoneCode($phone: String!) {
    sendPhoneVerificationCode(phone: $phone) {
      maskedPhone
    }
  }
`);

const CONFIRM_PHONE_CODE = gengql(/* GraphQL */ `
  mutation cognitoMfaConfirmPhoneCode($phone: String!, $code: String!) {
    confirmPhoneVerificationCode(phone: $phone, code: $code) {
      address
      verified
    }
  }
`);

export async function fetchCognitoMfaState(): Promise<CognitoMfaState> {
  const [preference, attributes] = await Promise.all([fetchMFAPreference(), fetchUserAttributes()]);
  return {
    enabled: preference.enabled ?? [],
    preferred: preference.preferred,
    email: attributes.email,
  };
}

// `turnOff`: the factors switched off in the same call, one method at a time (ENG-6323).
async function prefer(type: CognitoMfaType, turnOff?: CognitoMfaType[]): Promise<void> {
  try {
    await updateMFAPreference(preferOnly(type, turnOff));
  } catch (err) {
    mapError(err, 'code');
  }
}

/** Email codes go to the pool's email address, once `confirmEmail` proves it. */
export function enableEmailMfa(turnOff?: CognitoMfaType[]): Promise<void> {
  return prefer('EMAIL', turnOff);
}

/**
 * SMS MFA texts the pool's phone_number, which the api copies from the
 * account's primary phone once it is confirmed (ENG-6322); see `confirmPhone`.
 */
export function enableSmsMfa(turnOff?: CognitoMfaType[]): Promise<void> {
  return prefer('SMS', turnOff);
}

/**
 * Email a code to the pool's email (ENG-6323), proving the viewer reads it
 * before email codes go on. Cognito's attribute verification: it changes
 * neither the email nor its verified state. Resolves with Cognito's mask.
 */
export async function sendEmailVerification(): Promise<string | undefined> {
  try {
    const { destination } = await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
    return destination;
  } catch (err) {
    mapError(err, 'code');
  }
}

/** Confirm the emailed code; it leaves email_verified true and turns nothing on. */
export async function confirmEmail(code: string): Promise<void> {
  try {
    await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code.trim() });
  } catch (err) {
    if (err instanceof Error && err.name === 'ExpiredCodeException') {
      throw new CognitoSignInError(ERROR_CODES.OTP_EXPIRED, EMAIL_CODE_EXPIRED_MESSAGE, err);
    }
    mapError(err, 'code');
  }
}

/**
 * The api's refusals of a phone code, as the settings page words them. Its
 * OTP_INVALID means a bad code when confirming, but Twilio refusing the number
 * when sending.
 */
function mapPhoneError(err: unknown, step: 'send' | 'confirm'): never {
  const code = firstErrorCode(err);
  switch (code) {
    case ERROR_CODES.ADDRESS_ADDRESS_TAKEN:
      throw new CognitoSignInError(ERROR_CODES.ADDRESS_ADDRESS_TAKEN, PHONE_TAKEN_MESSAGE, err);
    case ERROR_CODES.CONTACT_PHONE_INVALID:
      throw new CognitoSignInError(ERROR_CODES.CONTACT_PHONE_INVALID, PHONE_FORMAT_MESSAGE, err);
    case ERROR_CODES.OTP_INVALID:
    case ERROR_CODES.OTP_SEND_FAILED:
      if (step === 'send') {
        throw new CognitoSignInError(code, PHONE_UNTEXTABLE_MESSAGE, err);
      }
      throw new CognitoSignInError(ERROR_CODES.AUTH_OTP_INVALID, 'Invalid code', err);
    case ERROR_CODES.OTP_EXPIRED:
      throw new CognitoSignInError(ERROR_CODES.OTP_EXPIRED, CODE_EXPIRED_MESSAGE, err);
    case ERROR_CODES.OTP_MAX_ATTEMPTS_EXCEEDED:
    case ERROR_CODES.OTP_RATE_LIMIT_EXCEEDED:
      throw new CognitoSignInError(ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS, 'Too many attempts', err);
  }
  throw err;
}

/**
 * Have the api text a code proving the viewer holds `phone`. A number another
 * account holds is refused before anything is sent.
 */
export async function sendPhoneVerification(client: GraphQLClient, phone: string): Promise<void> {
  try {
    await client.mutate(SEND_PHONE_CODE, { phone });
  } catch (err) {
    mapPhoneError(err, 'send');
  }
}

/**
 * Confirm the texted code: the api makes `phone` the account's verified
 * primary phone and has updated the pool's phone_number when this resolves. A
 * code works once, so a caller whose next step (`enableSmsMfa`) fails retries
 * that step alone.
 */
export async function confirmPhone(
  client: GraphQLClient,
  phone: string,
  code: string
): Promise<void> {
  try {
    await client.mutate(CONFIRM_PHONE_CODE, { phone, code: code.trim() });
  } catch (err) {
    mapPhoneError(err, 'confirm');
  }
}

/**
 * Turn one factor off. When it was the preferred one and another remains, the
 * first remaining factor becomes preferred, so sign-in does not stop to ask
 * which one to use. Resolves with the factors still enabled.
 */
export async function disableMfaFactor(
  type: CognitoMfaType,
  state: Pick<CognitoMfaState, 'enabled' | 'preferred'>
): Promise<CognitoMfaType[]> {
  const remaining = state.enabled.filter((t) => t !== type);
  const input: UpdateMFAPreferenceInput = { [PREFERENCE_KEY[type]]: 'DISABLED' };
  // Promote one when the preferred factor goes, or when none was preferred.
  if (remaining.length && (state.preferred === type || !state.preferred)) {
    input[PREFERENCE_KEY[remaining[0]]] = 'PREFERRED';
  }
  try {
    await updateMFAPreference(input);
  } catch (err) {
    mapError(err, 'code');
  }
  return remaining;
}

interface CognitoErrorBody {
  __type?: string;
  message?: string;
}

/**
 * Re-authentication before a factor is turned off: check the password with a
 * fresh `InitiateAuth`, outside Amplify, so the current session and its tokens
 * stay untouched. Any answer other than an error (tokens, or an MFA challenge)
 * proves the password; the challenge is left unanswered and lapses. A wrong
 * password throws `auth.invalid_credentials`.
 *
 * Accepted for now: a user whose preferred factor is SMS or email is sent a
 * code on every check (Cognito delivers it with the challenge) that is never
 * asked for.
 */
export async function verifyCognitoPassword(username: string, password: string): Promise<void> {
  const { cognitoUserPoolId: poolId, cognitoClientId: clientId } = config;
  if (!poolId || !clientId) {
    throw new Error('Cognito password check called without a configured user pool');
  }
  const region = config.cognitoRegion ?? poolId.split('_')[0];
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: { USERNAME: username, PASSWORD: password },
    }),
  });
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as CognitoErrorBody;
  const err = new Error(body.message ?? `Cognito InitiateAuth failed (${res.status})`);
  // Cognito names the exception in __type, sometimes with a namespace prefix.
  err.name = body.__type?.split('#').pop() ?? 'UnknownError';
  mapError(err, 'password');
}
